import { create } from 'zustand/react';

export type TradingMessageFeedbackRating = 'VALUABLE' | 'NOT_VALUABLE' | 'FAULTY';

export interface TradingMessageFeedbackEntry {
  rating: TradingMessageFeedbackRating;
  createdAt: number;
}

interface StoredFeedbackState {
  version: 1;
  entries: Record<string, TradingMessageFeedbackEntry>;
}

const STORAGE_KEY = 'trading.message-feedback.v1';
const MAX_ENTRY_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const isValidRating = (rating: unknown): rating is TradingMessageFeedbackRating =>
  rating === 'VALUABLE' || rating === 'NOT_VALUABLE' || rating === 'FAULTY';

const safeParseState = (raw: string | null): StoredFeedbackState => {
  if (!raw) return { version: 1, entries: {} };
  try {
    const parsed = JSON.parse(raw) as StoredFeedbackState;
    if (parsed?.version !== 1 || typeof parsed.entries !== 'object' || parsed.entries === null) {
      return { version: 1, entries: {} };
    }
    return parsed;
  } catch {
    return { version: 1, entries: {} };
  }
};

const cleanupExpiredEntries = (
  state: StoredFeedbackState,
  now: number
): { state: StoredFeedbackState; changed: boolean } => {
  const nextEntries: Record<string, TradingMessageFeedbackEntry> = {};
  let changed = false;

  Object.entries(state.entries).forEach(([key, value]) => {
    const createdAt = typeof value?.createdAt === 'number' ? value.createdAt : 0;
    const rating = value?.rating;
    if (createdAt > 0 && now - createdAt < MAX_ENTRY_AGE_MS && isValidRating(rating)) {
      nextEntries[key] = { rating, createdAt };
    } else {
      changed = true;
    }
  });

  if (!changed) return { state, changed: false };
  return { state: { version: 1, entries: nextEntries }, changed: true };
};

const loadFromStorage = (): StoredFeedbackState => {
  if (typeof window === 'undefined') return { version: 1, entries: {} };
  try {
    return safeParseState(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return { version: 1, entries: {} };
  }
};

const saveToStorage = (state: StoredFeedbackState): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    //
  }
};

const initialState = (): StoredFeedbackState => {
  const now = Date.now();
  const state = loadFromStorage();
  const { state: cleaned, changed } = cleanupExpiredEntries(state, now);
  if (changed) {
    saveToStorage(cleaned);
  }
  return cleaned;
};

export const buildTradingMessageFeedbackKey = (
  accountNumber: string | undefined,
  userId: string | undefined,
  messageId: number
): string => `${accountNumber ?? 'unknown'}:${userId ?? 'anonymous'}:${messageId}`;

interface TradingMessageFeedbackStore {
  entries: Record<string, TradingMessageFeedbackEntry>;
  cleanup: () => void;
  recordRating: (
    entryKey: string,
    rating: TradingMessageFeedbackRating
  ) => {
    didStore: boolean;
    rating: TradingMessageFeedbackRating | null;
  };
}

export const useTradingMessageFeedbackStore = create<TradingMessageFeedbackStore>((set, get) => ({
  entries: initialState().entries,

  cleanup: () => {
    const now = Date.now();
    const { state: cleaned, changed } = cleanupExpiredEntries({ version: 1, entries: get().entries }, now);
    if (!changed) return;
    set({ entries: cleaned.entries });
    saveToStorage(cleaned);
  },

  recordRating: (entryKey: string, rating: TradingMessageFeedbackRating) => {
    const now = Date.now();
    const { state: cleaned } = cleanupExpiredEntries({ version: 1, entries: get().entries }, now);
    const existing = cleaned.entries[entryKey];
    if (existing) {
      if (cleaned.entries !== get().entries) {
        set({ entries: cleaned.entries });
        saveToStorage({ version: 1, entries: cleaned.entries });
      }
      return { didStore: false, rating: existing.rating };
    }

    const nextEntries = {
      ...cleaned.entries,
      [entryKey]: { rating, createdAt: now },
    };

    set({ entries: nextEntries });
    saveToStorage({ version: 1, entries: nextEntries });
    return { didStore: true, rating };
  },
}));
